import { IHTMLPopUpTemplateConfig, IHTMLPopUpTemplateFormData, IShorthandStyle } from '../../types';

export const buildHTMLPopUpTemplatePayload = (formData: IHTMLPopUpTemplateFormData): IHTMLPopUpTemplateConfig => {
  const buildMarginStyling = (margin: IShorthandStyle): string =>
    `${margin.top || 0}px ${margin.right || 0}px ${margin.bottom || 0}px ${margin.left || 0}px`;

  const {
    containerWidth,
    containerBackgroundImageSrc,
    containerBackgroundImagePosition,
    containerBackgroundColorOpacity,
    topContainerMargin,
    merchantLogoWidth,
    merchantLogoMargin,
    textColor,
    textFontFamily,
    textTitleMargin,
    textMargin,
    buttonWidth,
    buttonPadding,
    buttonImageWidth,
    buttonImageMargin,
    buttonSpanCalloutColor,
    headerText,
    subText,
    merchantLogoSrc,
    merchantLogoAlt,
    buttonPrefixText,
    buttonTextCallout,
    buttonSuffixText,
    buttonImageSrc,
    buttonImageAlt,
    destinationURL,
  } = formData;

  const containerBackgroundColorOpacityValue = Number(containerBackgroundColorOpacity) || 75;

  return {
    containerWidth: `${containerWidth}px`,
    containerBackgroundImageSrc,
    containerBackgroundImagePosition: `${containerBackgroundImagePosition.x ||
      0}px ${containerBackgroundImagePosition.y || 0}px`,
    containerBackgroundColorOpacity: `${Math.round(containerBackgroundColorOpacityValue * 100)}`,
    topContainerMargin: buildMarginStyling(topContainerMargin),
    merchantLogoWidth: `${merchantLogoWidth}px`,
    merchantLogoMargin: buildMarginStyling(merchantLogoMargin),
    textColor,
    textFontFamily,
    textTitleMargin: buildMarginStyling(textTitleMargin),
    textMargin: buildMarginStyling(textMargin),
    buttonWidth: `${buttonWidth || 100}%`,
    buttonPadding: buildMarginStyling(buttonPadding),
    buttonImageWidth: `${buttonImageWidth || 18}px`,
    buttonImageMargin: buildMarginStyling(buttonImageMargin),
    buttonSpanCalloutColor,
    headerText,
    subText,
    merchantLogoSrc,
    merchantLogoAlt,
    buttonPrefixText,
    buttonTextCallout,
    buttonSuffixText,
    buttonImageSrc,
    buttonImageAlt,
    destinationURL,
  };
};

export const buildHTMLPopUpTemplateFormData = (config: IHTMLPopUpTemplateConfig): IHTMLPopUpTemplateFormData => {
  const parseMarginString = (marginString: string | undefined | null): IShorthandStyle => {
    // Parse CSS shorthand margin string (e.g., "10px 20px 30px 40px") into IShorthandStyle
    if (marginString === undefined || marginString === null || typeof marginString !== 'string') {
      return { top: '0', right: '0', bottom: '0', left: '0' };
    }

    const values = marginString.split(' ').map(value => value.replace('px', ''));

    if (values.length === 1) {
      // All sides same
      return { top: values[0], right: values[0], bottom: values[0], left: values[0] };
    } else if (values.length === 2) {
      // top/bottom, left/right
      return { top: values[0], right: values[1], bottom: values[0], left: values[1] };
    } else if (values.length === 3) {
      // top, left/right, bottom
      return { top: values[0], right: values[1], bottom: values[2], left: values[1] };
    } else if (values.length === 4) {
      // top, right, bottom, left
      return { top: values[0], right: values[1], bottom: values[2], left: values[3] };
    }

    // Fallback
    return { top: '0', right: '0', bottom: '0', left: '0' };
  };

  const parseBackgroundPosition = (positionString: string | undefined | null): { x: string; y: string } => {
    // Parse position string (e.g., "10px 20px") into x/y coordinates
    if (positionString === undefined || positionString === null || typeof positionString !== 'string') {
      return { x: '0', y: '0' };
    }

    const values = positionString.split(' ').map(value => value.replace('px', ''));
    return {
      x: values[0] || '0',
      y: values[1] || '0',
    };
  };

  const {
    containerWidth,
    containerBackgroundImageSrc,
    containerBackgroundImagePosition,
    containerBackgroundColorOpacity,
    topContainerMargin,
    merchantLogoWidth,
    merchantLogoMargin,
    textColor,
    textFontFamily,
    textTitleMargin,
    textMargin,
    buttonWidth,
    buttonPadding,
    buttonImageWidth,
    buttonImageMargin,
    buttonSpanCalloutColor,
    headerText,
    subText,
    merchantLogoSrc,
    merchantLogoAlt,
    buttonPrefixText,
    buttonTextCallout,
    buttonSuffixText,
    buttonImageSrc,
    buttonImageAlt,
    destinationURL,
  } = config;

  return {
    // Style properties - parse strings back to form data format
    containerWidth: containerWidth?.replace('px', '') || '0',
    containerBackgroundImageSrc,
    containerBackgroundImagePosition: parseBackgroundPosition(containerBackgroundImagePosition),
    containerBackgroundColorOpacity: (parseFloat(containerBackgroundColorOpacity || '0') / 100).toString(),
    topContainerMargin: parseMarginString(topContainerMargin),
    merchantLogoWidth: merchantLogoWidth?.replace('px', '') || '0',
    merchantLogoMargin: parseMarginString(merchantLogoMargin),
    textColor,
    textFontFamily,
    textTitleMargin: parseMarginString(textTitleMargin),
    textMargin: parseMarginString(textMargin),
    buttonWidth: buttonWidth?.replace(/%/g, '').replace(/px/g, '') || '100',
    buttonPadding: parseMarginString(buttonPadding),
    buttonImageWidth: buttonImageWidth?.replace('px', '') || '18',
    buttonImageMargin: parseMarginString(buttonImageMargin),
    buttonSpanCalloutColor,

    // Content properties - pass through as-is
    headerText,
    subText,
    merchantLogoSrc,
    merchantLogoAlt,
    buttonPrefixText,
    buttonTextCallout,
    buttonSuffixText,
    buttonImageSrc,
    buttonImageAlt,
    destinationURL,
  };
};
